import { defineStore } from "pinia";
import { computed, type MaybeRefOrGetter, ref, toValue } from "vue";

import {
  useBookmarksByPage,
  useBookmarksCount,
  useBookmarksStatus,
} from "@/composition/api/bookmarks.ts";
import type { ReducedContentItem } from "@/types/content.d.ts";

export type BookmarkOrder = "NEWEST_FIRST" | "OLDEST_FIRST";

export const useBookmarksStore = defineStore("bookmarks", () => {
  const bookmarksCount = ref(0);
  const bookmarks = ref<ReducedContentItem[]>([]);
  const currentPage = ref(0);
  const pageSize = ref(10);
  const activeSorting = ref<BookmarkOrder>("NEWEST_FIRST");
  const isFetchingBookmarks = ref(false);

  /**
   * Internal bookmark statuses
   * NOTE: keep as record for O(1) lookup and easy merging.
   */
  const internalStatuses = ref<Record<string, boolean> | null>({});

  const {
    data,
    execute: getBookmarkCount,
    onFetchFinally,
  } = useBookmarksCount();

  onFetchFinally(() => {
    bookmarksCount.value = data.value ?? 0;
  });

  const {
    data: bookmarkStatuses,
    post: postBookmarkStatuses,
    execute: executeBookmarkStatus,
  } = useBookmarksStatus({
    refetch: true,
    immediate: false,
    initialData: {},
  });

  const bookmarkedUrns = computed(() => {
    if (!internalStatuses.value) return undefined;
    return Object.entries(internalStatuses.value)
      .filter(([_, value]) => value)
      .map(([key]) => key);
  });

  const hasAnyBookmarks = computed(() => {
    if (!internalStatuses.value) return false;
    return Object.values(internalStatuses.value).some(
      (isBookmarked) => isBookmarked,
    );
  });

  const hasMoreBookmarks = computed(() => {
    // use server-reported total count as truth
    return bookmarks.value.length < bookmarksCount.value;
  });

  const bookmarksPagePayload = computed(() => ({
    pageNumber: currentPage.value,
    pageSize: pageSize.value,
    order: activeSorting.value,
  }));

  const { data: bookmarkPageData, execute: getBookmarksByPage } =
    useBookmarksByPage(bookmarksPagePayload, {
      immediate: false,
    });

  function mergeByUrn(
    existing: ReducedContentItem[],
    incoming: ReducedContentItem[],
  ): ReducedContentItem[] {
    const seen = new Set(existing.map((x) => x.urn));
    const merged = [...existing];
    for (const item of incoming) {
      if (!seen.has(item.urn)) {
        seen.add(item.urn);
        merged.push(item);
      }
    }
    return merged;
  }

  async function refreshBookmarkStatus(urn: string, status: boolean) {
    if (!internalStatuses.value) return;
    internalStatuses.value[urn] = status;
  }

  async function getBookmarkStatuses(urns: MaybeRefOrGetter<string[]>) {
    try {
      postBookmarkStatuses(toValue(urns));
      await executeBookmarkStatus();
      /**
       * Merge, not replace: another widget's concurrent fetch would
       * otherwise erase statuses this call doesn't include.
       */
      internalStatuses.value = {
        ...internalStatuses.value,
        ...bookmarkStatuses.value,
      };
    } catch (error) {
      // eslint-disable-next-line no-console -- Disable because of error log
      console.error(error);
    }
  }

  const handleBookmarkRemove = async (removedUrn: string) => {
    bookmarks.value = bookmarks.value.filter((item) => item.urn !== removedUrn);
    await getBookmarkCount();
  };

  const handleBookmarkAdd = async () => {
    await getBookmarkCount();
  };

  const handleRemoveMissing = async () => {
    bookmarks.value = bookmarks.value.filter((item) => !item.missing);
    await getBookmarkCount();
  };

  async function fetchBookmarks({ reset = false } = {}) {
    if (isFetchingBookmarks.value) return;
    if (!reset && !hasMoreBookmarks.value) return;

    isFetchingBookmarks.value = true;
    try {
      if (reset) {
        currentPage.value = 0;
        bookmarks.value = [];
      } else {
        currentPage.value += 1;
      }

      await getBookmarksByPage();

      const newBookmarks = bookmarkPageData.value?.bookmarkedContentList ?? [];
      bookmarks.value = mergeByUrn(bookmarks.value, newBookmarks);

      // Keep count in sync (prevents stale hasMore if count isn't fetched elsewhere)
      if (typeof bookmarkPageData.value?.totalElements === "number") {
        bookmarksCount.value = bookmarkPageData.value.totalElements;
      } else {
        // fallback to existing endpoint
        await getBookmarkCount();
      }

      const urns = newBookmarks.map((b) => b.urn);
      if (urns.length > 0) {
        await getBookmarkStatuses(urns);
      }
    } finally {
      isFetchingBookmarks.value = false;
    }
  }

  /**
   * Set sorting and reload from scratch.
   */
  async function setSorting(order: BookmarkOrder) {
    activeSorting.value = order;
    await fetchBookmarks({ reset: true });
  }

  return {
    bookmarks,
    bookmarksCount,
    bookmarkStatuses: internalStatuses,
    bookmarkedUrns,
    hasAnyBookmarks,
    currentPage,
    pageSize,
    activeSorting,
    isFetchingBookmarks,
    hasMoreBookmarks,
    getBookmarkCount,
    getBookmarkStatuses,
    refreshBookmarkStatus,
    fetchBookmarks,
    setSorting,
    handleBookmarkRemove,
    handleBookmarkAdd,
    handleRemoveMissing,
  };
});
